import { toast } from 'sonner';
import { copyImageToClipboard, copyTextToClipboard } from '../services/clipboardService';

/**
 * Hook for handling reference sharing functionality
 * Provides consistent copy image and copy link functionality across all view modes
 */
export const useReferenceSharing = () => {
  
  const copyReferenceImage = async (imageUrl: string | null | undefined): Promise<boolean> => {
    if (!imageUrl) {
      toast.error('No image available to copy');
      return false;
    }

    try {
      const success = await copyImageToClipboard(imageUrl);
      if (success) {
        toast.success('Image copied to clipboard!');
      } else {
        toast.error('Failed to copy image - your browser may not support this feature');
      }
      return success;
    } catch (error) {
      console.error('Error copying image:', error);
      toast.error('Failed to copy image');
      return false;
    }
  };
  
  const copyReferenceLink = async (referenceId: string): Promise<boolean> => {
    try {
      const shareUrl = `${window.location.origin}/reference/${referenceId}`;
      const success = await copyTextToClipboard(shareUrl);
      
      if (success) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Failed to copy link');
      }
      return success;
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Failed to copy link');
      return false;
    }
  };

  const copyReferenceData = async (reference: { _id: string; title?: string; sourceURL?: string | null }): Promise<boolean> => {
    try {
      const referenceData = [
        `Title: ${reference.title || 'Untitled'}`,
        `Link: ${window.location.origin}/reference/${reference._id}`,
        reference.sourceURL ? `Source: ${reference.sourceURL}` : null
      ].filter(Boolean).join('\n');

      const success = await copyTextToClipboard(referenceData);
      
      if (success) {
        toast.success('Reference details copied to clipboard!');
      } else {
        toast.error('Failed to copy reference details');
      }
      return success;
    } catch (error) {
      console.error('Error copying reference data:', error);
      toast.error('Failed to copy reference details');
      return false;
    }
  };
  
  return { 
    copyReferenceImage, 
    copyReferenceLink,
    copyReferenceData
  };
}; 